<template>

    <div class="container" style="padding-bottom:50px">
        <a-card>
            <template #title>
                <a-breadcrumb separator=">">
                    <a-breadcrumb-item>
                        <FileOutlined/>
                        <a href="/inspections"><span>Vistorias</span></a>
                    </a-breadcrumb-item>
                    <a-breadcrumb-item>Agendar Vistoria</a-breadcrumb-item>
                </a-breadcrumb>
            </template>

            <div>
                <a-form layout="vertical" :model="scheduleInfo" ref="scheduleInfoRef" name="scheduleForm"
                        @finish="submit" @finishFailed="openNotification('error', 'Ainda há campos pendentes')"
                >
                    <a-row :gutter="16">
                        <a-col :span="14">
                            <a-row :gutter="14">
                                <a-col :span="4">
                                    <a-form-item :name="'inspectionId'" label="Id da vistoria"
                                                 :rules="[{required:true, message:'Informe o id'}]"
                                    >
                                        <a-input v-model:value="scheduleInfo.inspectionId" disabled />
                                    </a-form-item>
                                </a-col>
                                <a-col :span="5">
                                    <a-form-item :name="`date`" label="Data"
                                                 :rules="[{required:true, message:'Informe uma data'}]"
                                    >
                                        <a-config-provider :locale="ptBR">
                                            <a-date-picker v-model:value="scheduleInfo.date" :format="'DD/MM/YYYY'" style="width: 100%" />
                                        </a-config-provider>
                                    </a-form-item>
                                </a-col>
                                <a-col :span="5">
                                    <a-form-item :name="`hour`" label="Horário"
                                                 :rules="[{required:true, message:'Informe um horário'}]"
                                    >
                                        <a-config-provider :locale="ptBR">
                                            <a-time-picker v-model:value="scheduleInfo.hour" format="HH:mm" value-format="HH:mm" style="width: 100%"
                                                           :minute-step="10" />
                                        </a-config-provider>
                                    </a-form-item>
                                </a-col>
                                <a-col :span="10">
                                    <a-form-item :name="'confirmPerson'" label="Nome do contribuinte"
                                                 :rules="[{required:true, message:'Informe o nome de quem confirmou o agendamento'}]"
                                    >
                                        <a-input v-model:value="scheduleInfo.confirmPerson" allow-clear />
                                    </a-form-item>
                                </a-col>
                            </a-row>
                            <a-row :gutter="14">
                                <a-col :span="12">
                                    <a-form-item :name="`telephone`" label="Telefone"
                                                 :rules="[{required:true, message:'Informe o telefone'}]"
                                    >
                                        <a-input v-model:value="scheduleInfo.telephone" type="tel" v-mask="'(##) #####-####'" />
                                    </a-form-item>
                                </a-col>
                                <a-col :span="12">
                                    <a-form-item :name="'email'" label="Email">
                                        <a-input v-model:value="scheduleInfo.email" allow-clear />
                                    </a-form-item>
                                </a-col>
                            </a-row>
                        </a-col>
                        <a-col :span="10">
                            <a-row>
                                <a-col :span="24">
                                    <a-form-item :name="'observations'" label="Observações"
                                    >
                                        <a-textarea v-model:value="scheduleInfo.observations" allow-clear :auto-size="{ minRows: 5, maxRows: 5 }" />
                                    </a-form-item>
                                </a-col>
                            </a-row>
                        </a-col>
                    </a-row>
                    <a-row>
                        <a-col :span="8">
                            <a-button type="primary" html-type="submit">Agendar</a-button>
                        </a-col>
                    </a-row>
                </a-form>
            </div>
        </a-card>
        <br />
        <a-card >
            <h2 class="h2-style">Histórico de agendamentos <span style="font-size:16px; color:grey">(Vistoria nº {{ scheduleInfo.inspectionId }})</span></h2>
            <a-table :dataSource="scheduleHistory" bordered :columns="historyColumns">
                <template #emptyText>
                    <a-empty :image="Empty.PRESENTED_IMAGE_SIMPLE" description="Não há agendamentos anteriores para esta vistoria" />
                </template>

                <template #bodyCell="{ column, record }">
                    <template v-if="column.dataIndex === 'images'">
                        <a-image
                            v-for="image in  record.images"
                            width="70px"
                            height="70px"
                            :src="image"
                        />
                    </template>
                </template>
            </a-table>
        </a-card>
    </div>
</template>

<script setup>
import {FileOutlined} from "@ant-design/icons-vue";
import {onMounted, ref} from "vue";
import {Empty, notification} from "ant-design-vue";
import ptBR from "ant-design-vue/lib/locale/pt_BR";
import { openNotification } from "../../../utils/utilities";

const props = defineProps(['user', 'inspectionId', 'history']);
const scheduleInfoRef = ref();
const scheduleInfo = ref({
    date: '',
    hour: '',
    user: '',
    observations: '',
    inspectionId: '',
    confirmPerson: '',
    telephone: '',
    email: ''
});
const scheduleHistory = ref([]);
const historyColumns = ref([
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        sorter: { compare: (a, b) => a.id - b.id },
        defaultSortOrder: 'descend',
        width: 100,
    },
    {
        title: 'Data e hora',
        dataIndex: 'dataHora',
        key: 'dataHora',
        width: 250
    },
    {
        title: 'Nome',
        dataIndex: 'nome',
        key: 'nome',
        width: 200,
    },
    {
        title: 'Telefone',
        dataIndex: 'telefone',
        key: 'telefone',
        width: 200,
    },
    {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
    },
    {
        title: 'Observações',
        dataIndex: 'observacoes',
        key: 'observacoes',
        width: 650,
    },
    {
        title: 'Imagens',
        dataIndex: 'images',
        key: 'images',
        width: 300,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 300,
        customCell: (record) => {
            let bg = '';
            switch (record.status) {
                case 'Pendente':
                    bg = '#fff7e6';
                    break;
                case 'Completado':
                    bg = '#e6f7ff';
                    break;
                case 'Não realizado':
                    bg = '#fff1f0';
                    break;
            }
            return {style: {background: bg}};
        },

    }
]);

onMounted(async () => {
    try {
        const response = (await window.axios.get(`api/inspections/${props.inspectionId}`)).data.data

        scheduleInfo.value.confirmPerson = response['requerente']
        scheduleInfo.value.telephone = response['telefone']
        scheduleInfo.value.email = response['email']

        scheduleInfo.value.user = props.user
        scheduleInfo.value.inspectionId = props.inspectionId

        scheduleHistory.value = props.history
        if (props.history.length > 0) {
            const schedule = props.history[0]
            scheduleInfo.value.observations = schedule.observacoes
            scheduleInfo.value.confirmPerson = schedule.nome
        }
    } catch (error) {
        openNotification('error', error.message)
    }
})

const submit = async () =>  {
    try {
        await window.axios.post('/api/inspections/schedule', scheduleInfo.value)

        openNotification('success', 'Agendamento realizado com sucesso')
        window.location.href = '/inspections'
    } catch (error) {
        openNotification('error', error.response.data.message)
    }

}
</script>

<style scoped>
.h2-style {
    padding-left: 30px;
    font-weight: bold;
    font-family: Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji
}
</style>